import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import MobileNavigation from './MobileNavigation';
import MobileUserModal from './MobileUserModal';
import './Header.css';
import './modal-override.css';
import './enhanced-mobile.css';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userDropdownPosition, setUserDropdownPosition] = useState({ top: 80, right: 10 });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const userDropdownRef = useRef(null);
  const userToggleRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdowns on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setUserDropdownOpen(false);
        setMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Track window width for responsive dropdown
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  return (
    <>
      <header className="main-header">
        <div className="header-container">
          <div className="logo">
            <Link to="/dashboard" className="logo-link">
              <img src="/images/WhiteRabbitweb.png" alt="White Rabbit" className="rabbit-logo" />
              <span className="logo-text">White Rabbit</span>
            </Link>
          </div>
          
          {/* Mobile hamburger menu */}
          {windowWidth <= 768 && (
            <div className="mobile-header-controls">
              {user && <NotificationBell />}
              <button 
                className={`mobile-menu-toggle ${menuOpen ? 'active' : ''}`}
                onClick={toggleMenu}
                aria-label="Toggle navigation menu"
              >
                ☰
              </button>
            </div>
          )}

          {/* Desktop navigation */}
          {windowWidth > 768 && (
            <nav className="main-nav">
              <ul>
                <li>
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li>
                  <Link to="/hack-the-planet">Hack The Planet</Link>
                </li>
                <li>
                  <Link to="/lifestyle">Life Style</Link>
                </li>
                <li>
                  <Link to="/top-hacker">Leader Board</Link>
                </li>
                {user && user.role === 'admin' && (
                  <li>
                    <Link to="/admin" className="admin-link">
                      Admin Panel
                    </Link>
                  </li>
                )}
                {user && (
                  <li>
                    <Link to="/chat" className="deepseek-chat-link">
                      <img src="/images/WhiteRabbitweb.png" alt="" className="chat-rabbit-icon" />
                      White Rabbit
                    </Link>
                  </li>
                )}
              </ul>
            </nav>
          )}
          
          {/* Desktop user controls */}
          {windowWidth > 768 && (
            <div className="user-info">
              {user && (
                <>
                  <NotificationBell />
                  <div className={`user-panel ${userDropdownOpen ? 'open' : ''}`} ref={userDropdownRef}>
                    <button 
                      ref={userToggleRef}
                      className="user-toggle" 
                      onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    >
                      <span className="username">{user.username}</span>
                      <span className="user-indicator">▼</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>
      
      {/* Enhanced Mobile Navigation */}
      <MobileNavigation 
        isOpen={menuOpen && windowWidth <= 768} 
        onClose={() => setMenuOpen(false)} 
      />
      
      {/* Enhanced Mobile User Modal */}
      <MobileUserModal 
        isOpen={userDropdownOpen && windowWidth <= 768} 
        onClose={() => setUserDropdownOpen(false)} 
      />
      
      {/* Desktop User dropdown portal */}
      {userDropdownOpen && windowWidth > 768 && createPortal(
        <div 
          className="user-dropdown open"
          ref={userDropdownRef}
          style={{
            position: 'fixed',
            top: '70px',
            right: '20px',
            zIndex: 2147483647
          }}
        >
          <div className="user-stats">
            <div className="stat-item">
              <span className="stat-label">XP:</span>
              <span className="stat-value">{user.xp || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">WR Coins:</span>
              <span className="stat-value">{parseFloat(user.wrCoins || 0).toFixed(3)}</span>
            </div>
          </div>
          <div className="survival-status">
            <h4 className="survival-title">🍔 Food/Drink Supplies</h4>
            <div className="survival-bars">
              <div className="survival-item">
                <span className="survival-label">🥤 Food/Drink:</span>
                <div className="survival-bar">
                  <div 
                    className="survival-fill fooddrink" 
                    style={{ width: `${((user.foodDrink?.currentSupplies || 10) / 10) * 100}%` }}
                  ></div>
                  <span className="survival-percent">{user.foodDrink?.currentSupplies || 10}/10</span>
                </div>
              </div>
            </div>
            <div className="survival-info">
              {(user.foodDrink?.currentSupplies || 10) > 0 ? (
                <span className="survival-status-text healthy">✅ {user.foodDrink?.currentSupplies || 10}/10 moves available</span>
              ) : (
                <span className="survival-status-text emergency">⚠️ No supplies - 3 emergency moves every 12 hours</span>
              )}
            </div>
          </div>
          <div className="user-actions">
            <Link to="/profile" className="user-action-btn" onClick={() => setUserDropdownOpen(false)}>
              👤 Profile
            </Link>
            <button onClick={handleLogout} className="user-action-btn logout">
              🚪 Logout
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Header;